import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react'
import useTranslate from '../hooks/useTranslate';
import scrollTop from '../helpers/scrollTop'
import Context from '../context'
import addToCart from '../helpers/addToCart'
import wishlistHelper from '../helpers/wishlistHelper'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FaStar, FaHeart, FaRegHeart, FaCheckCircle } from 'react-icons/fa'
import { MdDelete } from "react-icons/md"
import SummaryApi from '../common'
import service from '../common/HttpService'
import { getCookie } from '../common/cookieHelper'

const VerticalCard = ({ loading: externalLoading, data: externalData = [] }) => {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)
    const loadingList = new Array(8).fill(null)
    const { fetchUserAddToCart, cartItems, fetchUserWishlist, wishlistItems, setShowCart, translateText, languageCode } = useContext(Context)
    const user = useSelector(state => state?.user?.user)
    const navigate = useNavigate()

    const t = useTranslate({
        off: "OFF",
        save: "SAVE",
        viewInCart: "View in Cart",
        addToCartLabel: "Add To Cart"
    });

    const [translatedData, setTranslatedData] = useState([])

    const isInWishlist = (productId) => {
        return (wishlistItems || []).some(item => item._id === productId)
    }

    const mapProductData = (allProducts) => {
        const backendBaseUrl = (SummaryApi.allProduct.url || "").split('/Api')[0]

        return allProducts.map(prod => {
            const ratesArray = prod.item_rates || prod.rates || [];
            const mainRate = ratesArray.length > 0 ? ratesArray[0] : {};
            const itemImages = prod.productImage || prod.image || [];
            const imagesArray = Array.isArray(itemImages) ? itemImages : [itemImages].filter(Boolean);

            const processedImages = imagesArray.map(img => {
                if (typeof img === 'string' && img && !img.startsWith('http')) {
                    const cleanPath = img.startsWith('/') ? img.substring(1) : img;
                    return `${backendBaseUrl}/${cleanPath}`;
                }
                return img;
            }).filter(Boolean);

            if (processedImages.length === 0) {
                processedImages.push('https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ2sSeQqjaUTuZ3gRgkKjidpaipF_l6s72lBw&s');
            }

            return {
                ...prod,
                _id: prod.id || prod._id,
                id: prod.id || prod._id,
                productName: prod.itemname || prod.productName || "Product",
                name: prod.vendor_name || prod.brandName || prod.name || "FasalShop",
                productImage: processedImages,
                price: Number(mainRate.mrp) || Number(prod.price) || 0,
                sellingPrice: Number(mainRate.salerate) || Number(prod.sellingPrice) || 0,
                unit: mainRate.unit_name || prod.unit || "Unit",
                brandName: prod.vendor_name || prod.brandName || "FasalShop",
                rates: prod.item_rates || prod.rates || [],
                vendor_id: prod.vendor_id || prod.user_id || "1"
            };
        });
    };

    useEffect(() => {
        if (externalData) {
            const mapped = mapProductData(externalData);
            setData(mapped);
            setLoading(externalLoading);
        }
    }, [externalData, externalLoading]);

    useEffect(() => {
        const translateProducts = async () => {
            if (!languageCode || languageCode === 'en' || !data.length) {
                setTranslatedData(data);
                return;
            }
            try {
                const translated = await Promise.all(
                    data.map(async (prod) => ({
                        ...prod,
                        productName: await translateText(prod.productName, languageCode),
                        brandName: await translateText(prod.brandName, languageCode),
                        unit: await translateText(prod.unit, languageCode)
                    }))
                );
                setTranslatedData(translated);
            } catch (error) {
                console.error("VerticalCard translation error:", error);
                setTranslatedData(data);
            }
        };
        translateProducts();
    }, [languageCode, translateText, data]);

    const handleAddToCart = async (e, product) => {
        e?.preventDefault();
        const userId = user?._id || user?.id || user?.user_id || getCookie('user_id');
        if (!userId) {
            navigate("/login");
            return;
        }
        await addToCart(e, product, userId)
        fetchUserAddToCart()
    }

    const handleWishlist = (e, product) => {
        e?.preventDefault();
        e?.stopPropagation();
        const userId = user?._id || user?.id || user?.user_id || getCookie('user_id');
        if (!userId) {
            navigate("/login");
            return;
        }
        wishlistHelper.addToWishlist(e, product)
        fetchUserWishlist()
    }

    const updateQty = async (e, id, qty, isDummy, dummyId, change) => {
        e?.stopPropagation()
        e?.preventDefault()

        if (isDummy) {
            let cart = JSON.parse(localStorage.getItem('dummyCart')) || []
            const item = cart.find(i => i.productId === dummyId)
            if (item) {
                if (change === -1 && item.quantity === 1) {
                    cart = cart.filter(i => i.productId !== dummyId)
                } else {
                    item.quantity += change
                }
                localStorage.setItem('dummyCart', JSON.stringify(cart))
                fetchUserAddToCart()
            }
            return
        }

        const newQty = qty + change
        if (newQty <= 0) {
            const response = await service.post(SummaryApi.delete_cart.url, {
                id: id,
                user_id: user?._id || user?.id
            })
            if (response.success === "true" || response.success === true) {
                fetchUserAddToCart()
            }
        } else {
            const response = await service.post(SummaryApi.update_cart.url, {
                id: id,
                qty: newQty,
                user_id: user?._id || user?.id
            })
            if (response.success === "true" || response.success === true) {
                fetchUserAddToCart()
            }
        }
    }

    return (
        <div className='grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-7 transition-all'>
            {loading ? (
                loadingList.map((_, index) => (
                    <div key={"loadingvert" + index} className='w-full bg-white rounded-[1.5rem] border border-slate-100 shadow-sm overflow-hidden animate-pulse'>
                        <div className='h-48 bg-slate-50/50'></div>
                        <div className='p-5 space-y-4'>
                            <div className='h-3 bg-slate-100 w-1/3 rounded-full'></div>
                            <div className='h-4 bg-slate-100 w-full rounded-full'></div>
                            <div className='h-10 bg-slate-100 w-full rounded-xl mt-4'></div>
                        </div>
                    </div>
                ))
            ) : (
                translatedData.map((product, index) => {
                    const cartItem = cartItems?.find(item => {
                        const itemProdId = String(item.productId?._id || item.productId?.id || item.product_id || item.productId || "");
                        const currentProdId = String(product?._id || product?.id || "");
                        return itemProdId === currentProdId && itemProdId !== "";
                    })
                    const isInCart = !!cartItem

                    const discount = product?.price > product?.sellingPrice
                        ? Math.round(((product.price - product.sellingPrice) / product.price) * 100)
                        : 0;

                    return (
                        <div key={product?._id || index} className='w-full bg-white rounded-[1.5rem] flex flex-col group/card relative overflow-hidden transition-all duration-300 border border-slate-100 shadow-[0_4px_15px_-3px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_40px_-15px_rgba(0,178,89,0.12)] hover:-translate-y-2'>

                            {/* Discount Badge */}
                            {discount > 0 && (
                                <div className='absolute top-3 left-3 z-20 bg-[#00b259] text-white text-[10px] font-black px-2.5 py-1 rounded-lg shadow-md tracking-wider uppercase'>
                                    {discount}% {t.off}
                                </div>
                            )}

                            {/* Wishlist Button */}
                            <button
                                className={`absolute top-3 right-3 z-20 p-2.5 rounded-full shadow-md transition-all duration-300 bg-white hover:bg-slate-50 border border-slate-100 ${isInWishlist(product?._id) ? 'text-[#ff4757]' : 'text-slate-300 hover:text-[#ff4757]'}`}
                                onClick={(e) => handleWishlist(e, product)}
                            >
                                <FaHeart size={14} />
                            </button>

                            {/* Image Area */}
                            <Link to={"/product/" + product?._id} className='h-[180px] p-6 flex justify-center items-center overflow-hidden bg-slate-50/30 relative' onClick={scrollTop}>
                                <img
                                    src={Array.isArray(product?.productImage) && product.productImage.length > 0 ? product.productImage[0] : 'https://placehold.co/400x400/eaeff6/2a4b7c?text=FasalShop'}
                                    className='object-contain h-full w-full group-hover/card:scale-105 transition-transform duration-500 ease-out z-10'
                                    alt={product?.productName || 'Product'}
                                />
                                <div className='absolute inset-0 bg-black/[0.02] opacity-0 group-hover/card:opacity-100 transition-opacity'></div>
                            </Link>

                            {/* Details Area */}
                            <div className='p-5 flex flex-col flex-grow'>
                                <div className='flex items-center justify-between mb-3'>
                                    <div className='flex items-center bg-[#00b259]/10 text-[#00b259] px-2 py-0.5 rounded text-[10px] font-black gap-1'>
                                        {product?.rating || '5.0'} <FaStar size={8} />
                                    </div>
                                    <p className='text-slate-400 text-[9px] font-black tracking-widest uppercase truncate max-w-[80px]'>
                                        {product?.brandName || 'FasalShop'}
                                    </p>
                                </div>

                                <Link to={"/product/" + product?._id} onClick={scrollTop} className='flex-grow'>
                                    <h2 className='text-slate-800 text-[14px] md:text-[15px] font-black line-clamp-2 leading-tight mb-4 group-hover/card:text-[#00b259] transition-colors uppercase tracking-tight'>
                                        {product?.productName}
                                    </h2>
                                </Link>

                                {/* Price block */}
                                <div className='flex items-center justify-between mb-5'>
                                    <div className='flex flex-col'>
                                        <div className='flex items-center gap-1.5'>
                                            <span className='text-slate-900 font-black text-lg'>₹{product?.sellingPrice}</span>
                                            {product.price > product.sellingPrice && (
                                                <span className='text-slate-300 text-[11px] line-through font-bold'>₹{product.price}</span>
                                            )}
                                        </div>
                                        <span className='text-[9px] text-[#00b259] font-black uppercase tracking-widest mt-0.5 leading-none'>
                                            {t.save} ₹{product.price - product.sellingPrice}
                                        </span>
                                    </div>
                                    <div className='bg-slate-50 px-2 py-1 rounded-lg border border-slate-100'>
                                        <span className='text-[10px] text-slate-600 font-black whitespace-nowrap'>{product?.unit || '1 Unit'}</span>
                                    </div>
                                </div>

                                {/* Add To Cart Area */}
                                <div className='mt-auto'>
                                    {isInCart ? (
                                        <button
                                            className='relative overflow-hidden w-full h-11 md:h-12 bg-white border border-[#143d2c] text-[#143d2c] rounded-[10px] uppercase font-black tracking-[0.4px] leading-none flex items-center justify-center select-none group'
                                            onClick={(e) => {
                                                e?.preventDefault();
                                                setShowCart(true);
                                            }}
                                        >
                                            <div className='absolute -bottom-[50%] left-0 w-full h-full bg-[#143d2c] origin-bottom transition-transform duration-[600ms] ease-[cubic-bezier(0.48,0,0.12,1)] -skew-y-[9.3deg] scale-y-0 group-hover:scale-y-[2] z-0'></div>
                                            <span className='relative z-10 transition-all duration-[600ms] ease-[cubic-bezier(0.48,0,0.12,1)] group-hover:text-white flex items-center justify-center gap-2 text-[10px] md:text-[11px] group-hover:-translate-y-8 group-hover:opacity-0'>
                                                {t.viewInCart}
                                            </span>
                                            <span className='absolute top-[50%] left-[50%] -translate-x-[50%] translate-y-[150%] opacity-0 z-20 text-white transition-all duration-[600ms] ease-[cubic-bezier(0.48,0,0.12,1)] group-hover:-translate-y-[50%] group-hover:opacity-100 flex items-center justify-center gap-2 text-[10px] md:text-[11px] w-full'>
                                                {t.viewInCart}
                                            </span>
                                        </button>
                                    ) : (
                                        <button
                                            className='relative overflow-hidden w-full h-11 md:h-12 bg-white border border-[#00b259] text-[#00b259] rounded-[10px] uppercase font-black tracking-[0.4px] leading-none flex items-center justify-center select-none group'
                                            onClick={(e) => handleAddToCart(e, product)}
                                        >
                                            <div className='absolute -bottom-[50%] left-0 w-full h-full bg-[#143d2c] origin-bottom transition-transform duration-[600ms] ease-[cubic-bezier(0.48,0,0.12,1)] -skew-y-[9.3deg] scale-y-0 group-hover:scale-y-[2] z-0'></div>
                                            <span className='relative z-10 transition-all duration-[600ms] ease-[cubic-bezier(0.48,0,0.12,1)] group-hover:text-white flex items-center justify-center gap-2 text-[10px] md:text-[11px] group-hover:-translate-y-8 group-hover:opacity-0'>
                                                {t.addToCartLabel}
                                            </span>
                                            <span className='absolute top-[50%] left-[50%] -translate-x-[50%] translate-y-[150%] opacity-0 z-20 text-white transition-all duration-[600ms] ease-[cubic-bezier(0.48,0,0.12,1)] group-hover:-translate-y-[50%] group-hover:opacity-100 flex items-center justify-center gap-2 text-[10px] md:text-[11px] w-full'>
                                                {t.addToCartLabel}
                                            </span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )
                })
            )}
        </div>
    )
}

export default VerticalCard